import React, { useCallback, useRef, useState, useLayoutEffect } from 'react';

import useOutsideClick from 'hooks/useOutsideClick';
import useStoreTranslation from 'hooks/useStoreTranslation';

import { CountryCode } from '../..';
import { Container, HiddenSearchInput, NoResults, Items, Item, ItemCode } from './styles';

interface Props {
  show: boolean;
  value: CountryCode;
  countries?: CountryCode[];
  selectRef: React.RefObject<HTMLDivElement>;
  onChange: (value: CountryCode) => void;
  onClose: () => void;
}

interface OptionProps {
  className: string;
  code: string;
  name: string;
  onClick: (e: React.MouseEvent<HTMLLIElement>) => void;
}

const Option = ({ code, name, ...rest }: OptionProps) => (
  <Item {...rest}>
    <ItemCode>{code}</ItemCode>
    {name}
  </Item>
);

const LETTERS_REGEX = /[^A-Za-záéíóúàèìòùâêîôûäëïöüãẽĩõũÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÄËÏÖÜÃẼĨÕŨnÑçÇ ]/g;

const CountryCodeOptions = ({ show, countries, selectRef, value, onChange, onClose }: Props) => {
  const { t } = useStoreTranslation();

  const [searchText, setSearchText] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);

  useOutsideClick(selectRef, onClose);
  useLayoutEffect(() => {
    if (show && searchRef.current) searchRef.current.focus();
    else setSearchText('');
  }, [show]);

  const isActive = useCallback((item: CountryCode) => item.id === value.id, [value]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value.replace(LETTERS_REGEX, '').trim());
  };

  const handleItemClick = (country: CountryCode) => {
    if (country.id !== value.id) {
      onChange(country);
    }
    onClose();
  };

  const filteredList = (countries || []).filter((country) =>
    country.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  return show ? (
    <Container>
      <HiddenSearchInput ref={searchRef} onChange={handleSearchChange} />
      <Items>
        {filteredList.length === 0 ? (
          <NoResults>{t('countryCodeSelect.empty')}</NoResults>
        ) : (
          filteredList.map((country) => (
            <Option
              key={country.id}
              className={isActive(country) ? 'active' : ''}
              code={country.code}
              name={country.name}
              onClick={() => handleItemClick(country)}
            />
          ))
        )}
      </Items>
    </Container>
  ) : null;
};

export default CountryCodeOptions;
